import React, { useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { handleLoginWithAzure, popIntendedUrl } from "./../auth";
import { useAuthenticationToken } from "../context/AuthenticationTokenContext";
import { useNavigate } from "../url";
import { useAuth } from "../context/authProvider";

const LoginButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#fff",
  color: theme.palette._components.alert.info.color,
  width: "100%",
  height: "48px",
  border: `1px solid #E9EAED`,
  fontWeight: 500,
  fontSize: "1.6rem",
  "&:hover": {
    backgroundColor: theme.palette.primary.light,
  },
}));

const MicrosoftIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    width="24px"
    height="24px"
    style={{ flexShrink: 0, marginRight: "8px" }}
    {...props}
  >
    <path fill="#f1511b" d="M22 22H3V3h19v19z" />
    <path fill="#80cc28" d="M45 22H26V3h19v19z" />
    <path fill="#00a1f1" d="M22 45H3V26h19v19z" />
    <path fill="#fbbc09" d="M45 45H26V26h19v19z" />
  </svg>
);

const LoginPage = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { stayLoggedIn, setStayLoggedIn } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [authToken, setAuthToken] = useAuthenticationToken();

  React.useEffect(() => {
    if (authToken != null) {
      setAuthToken(null);
    }
  }, []);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const loginResponse = await instance.loginPopup({
        scopes: [
          "openid",
          "profile",
          "email",
          "Directory.Read.All",
          "GroupMember.Read.All",
          "User.Read",
        ],
        prompt: "select_account",
      });

      instance.setActiveAccount(loginResponse.account);

      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          scopes: [
            "openid",
            "profile",
            "email",
            "Directory.Read.All",
            "GroupMember.Read.All",
            "User.Read",
          ],
          account: loginResponse.account,
        });
      } catch (error) {
        console.warn("Silent token failed, using popup:", error);
        tokenResponse = await instance.acquireTokenPopup({
          scopes: [
            "openid",
            "profile",
            "email",
            "Directory.Read.All",
            "GroupMember.Read.All",
            "User.Read",
          ],
          account: loginResponse.account,
        });
      }

      const backendToken = await handleLoginWithAzure(
        tokenResponse,
        stayLoggedIn,
      );
      if (backendToken) setAuthToken(backendToken);

      const intendedUrl = popIntendedUrl();
      navigate(intendedUrl || "/", { replace: true });
    } catch (error) {
      console.error("Login failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h1" sx={{ fontSize: "3.6rem", mb: 4 }}>
        {t("login.title", "Přihlášení do systému")}
      </Typography>
      <Stack spacing={1}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {t("login.description", "Přihlaste se pomocí e-mailu a hesla")}
        </Typography>
        <LoginButton
          variant="contained"
          startIcon={isLoading ? null : <MicrosoftIcon />}
          onClick={handleLogin}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            t("login.loginButton", "Přihlásit se Microsoft účtem")
          )}
        </LoginButton>
        <FormControlLabel
          control={
            <Checkbox
              checked={stayLoggedIn}
              onChange={(e) => setStayLoggedIn(e.target.checked)}
            />
          }
          label={t("login.stayLoggedIn", "Nechte mě přihlášeného")}
        />
      </Stack>
    </>
  );
};

export default LoginPage;
